import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useUIStore, StatusKind } from '@/state/store';
import { useReachability, CapabilityState } from '@/hooks/useReachability';
import { useDeviceHealth, type DevState } from '@/hooks/useDeviceHealth';
import { clearToken, getDisplayName, getTenantName, getTenantLogo, apiFetch } from '@/lib/auth';
import { getPendingDocCountSync } from '@/lib/localDocs';
import { autoReconnectHeadset } from '@/lib/headsetTelephony';

// pre87d: Beim Abmelden warnen, wenn noch nicht abgelegte lokale Session-Dokumente vorliegen.
function confirmLogout(): boolean {
  const n = getPendingDocCountSync();
  if (n > 0) {
    return window.confirm(
      `Achtung: Es liegen noch ${n} nicht abgelegte Dokument${n === 1 ? '' : 'e'} aus Gesprächen lokal in diesem Browser. ` +
      `Wenn du dich abmeldest, kannst du sie ggf. nicht mehr herunterladen. Bitte lade sie vorher in der Nachbearbeitung herunter.\n\n` +
      `Trotzdem abmelden?`,
    );
  }
  return window.confirm('Wirklich abmelden?');
}

const STATUS_OPTS: { id: StatusKind; label: string }[] = [
  { id: 'available', label: 'Verfügbar' },
  { id: 'pause', label: 'Pause' },
  { id: 'wrap', label: 'Nachbereitung' },
  { id: 'phone', label: 'Besetzt' },
];

export function StatusSwitcher() {
  const status = useUIStore((s) => s.status);
  const setStatus = useUIStore((s) => s.setStatus);
  const phoneSource = useUIStore((s) => s.phoneSource);
  const phoneConfigured = useUIStore((s) => s.phoneConfigured);
  const setPhoneConfigured = useUIStore((s) => s.setPhoneConfigured);
  const setView = useUIStore((s) => s.setView);
  const view = useUIStore((s) => s.view);
  const wrapRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);

  // pre151: Ist eine Telefonie-Schnittstelle konfiguriert? (steuert Automatik-Badge)
  useEffect(() => {
    let alive = true;
    Promise.all([
      apiFetch('/api/telephony/webex/status').then((r) => (r.ok ? r.json() : null)).catch(() => null),
      apiFetch('/api/telephony/graph/status').then((r) => (r.ok ? r.json() : null)).catch(() => null),
    ]).then(([w, g]: [{ configured?: boolean } | null, { configured?: boolean } | null]) => {
      if (alive) setPhoneConfigured(!!(w?.configured || g?.configured));
    });
    return () => { alive = false; };
  }, [setPhoneConfigured]);

  // pre187: Beim Wechsel in die Nachbereitung den eigenen Status-Button mitziehen.
  // Feuert nur bei View-Wechsel → ein späteres manuelles 'Verfügbar' bleibt erhalten.
  useEffect(() => {
    if (view === 'wrap') setStatus('wrap');
  }, [view, setStatus]);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const ind = indicatorRef.current;
    if (!wrap || !ind) return;
    const active = wrap.querySelector('.status-opt.active') as HTMLElement | null;
    if (active) {
      const wr = wrap.getBoundingClientRect();
      const ar = active.getBoundingClientRect();
      ind.style.left = ar.left - wr.left + 'px';
      ind.style.width = ar.width + 'px';
    }
  }, [status, phoneSource, phoneConfigured]);

  // Selbst-Override gewinnt immer: 'Verfügbar' macht trotz besetzter Leitung wieder
  // erreichbar (phone:busy=false); 'Telefon' setzt manuell auf besetzt (phone:busy=true).
  const pick = (id: StatusKind) => {
    if (id === 'wrap') setView('wrap');
    setStatus(id); // setzt phoneSource: 'manual' bei 'phone', sonst null
    if (id === 'phone') {
      window.dispatchEvent(new CustomEvent('bamoa:phone-busy', { detail: { busy: true } }));
    } else {
      window.dispatchEvent(new CustomEvent('bamoa:my-status', { detail: { status: id } }));
      if (id === 'available') {
        window.dispatchEvent(new CustomEvent('bamoa:phone-busy', { detail: { busy: false } }));
      }
    }
  };

  return (
    <div className="status-switcher" ref={wrapRef}>
      <div className="status-indicator" ref={indicatorRef}></div>
      {STATUS_OPTS.map((o) => (
        <div
          key={o.id}
          className={`status-opt ${status === o.id ? 'active' : ''}`}
          data-status={o.id}
          onClick={() => pick(o.id)}
        >
          <span className="dot"></span>
          {o.label}
          {o.id === 'phone' && status === 'phone' && phoneConfigured && (
            <span className="status-src">{phoneSource === 'auto' ? 'auto' : 'man.'}</span>
          )}
        </div>
      ))}
    </div>
  );
}

// pre151: Der frühere TK-Frei/Besetzt-Chip ist in den Status-Umschalter
// integriert (Segment „Telefon"). Spiegelung A/V ↔ Companion weiterhin über
// 'agent:phone-status'; Routing-Ausschluss über 'phone:busy'.

// pre170/171: Erreichbarkeit als kompaktes Status-Symbol (links neben dem Zahnrad).
// Klick öffnet wieder das gewohnte Popup (Capability-Liste + „Als App installieren"),
// ergänzt um einen Link in den Erreichbarkeit-Tab der Einstellungen.
function ReachIcon() {
  const { caps, overall } = useReachability();
  const open = useUIStore((s) => s.reachPopoverOpen);
  const toggle = useUIStore((s) => s.toggleReachPopover);
  const showPwa = useUIStore((s) => s.showPwaToast);
  const openSettings = useUIStore((s) => s.openSettings);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) toggle();
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [open, toggle]);

  const color = overall === 'green' ? 'var(--status-available)' : overall === 'yellow' ? 'var(--warning)' : 'var(--danger)';
  const label = overall === 'green' ? 'Erreichbar' : overall === 'yellow' ? 'Eingeschränkt erreichbar' : 'Nicht erreichbar';

  return (
    <div
      className={`reach-chip ${open ? 'expanded' : ''}`}
      ref={ref}
      onClick={(e) => { e.stopPropagation(); toggle(); }}
      title={`Erreichbarkeit · ${label}`}
      style={{ background: 'transparent', border: 'none', padding: 0, width: 32, height: 32, justifyContent: 'center', color }}
    >
      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M10.3 21a2 2 0 0 0 3.4 0" />
      </svg>
      <div className="reach-popover" onClick={(e) => e.stopPropagation()} style={{ color: 'var(--fg)' }}>
        <div className="reach-pop-title">Hintergrund-Erreichbarkeit</div>
        <CapRow icon="🔔" name="Benachrichtigungen" state={caps.notifications} />
        <CapRow icon="📡" name="Web Push (Service Worker)" state={caps.webPush} />
        <CapRow icon="⚡" name="Wake Lock" state={caps.wakeLock} />
        <CapRow icon="📱" name="Als App installiert" state={caps.pwaInstalled} />
        <CapRow icon="🔊" name="Audio-Heartbeat" state={caps.audioHeartbeat} suffix=" (Backup)" />
        {caps.pwaInstalled !== 'ok' && (
          <button className="reach-pop-action" onClick={() => { showPwa(); toggle(); }}>
            Als App installieren →
          </button>
        )}
        <button
          className="reach-pop-action"
          onClick={() => { openSettings('reach'); toggle(); }}
          style={{ marginTop: 8, background: 'transparent', border: '1px solid var(--hairline)', color: 'var(--fg-soft)' }}
        >
          In den Einstellungen verwalten →
        </button>
      </div>
    </div>
  );
}

// pre180 (Punkt 7, passiv): Hinweis nur bei blockierter oder fehlender Kamera/Mikrofon.
// Rein passiv (Permissions-API + enumerateDevices), ohne die Kamera zu öffnen.
// Klick öffnet die Geräte-Einstellungen. Bei „ok"/„prompt" wird nichts angezeigt.
function DeviceHealthChip() {
  const health = useDeviceHealth();
  const openSettings = useUIStore((s) => s.openSettings);
  if (health.overall !== 'bad') return null;

  const word = (s: DevState) => (s === 'denied' ? 'blockiert' : s === 'missing' ? 'fehlt' : '');
  const problems = ([
    { name: 'Kamera', st: health.camera },
    { name: 'Mikrofon', st: health.microphone },
  ] as { name: string; st: DevState }[]).filter((d) => d.st === 'denied' || d.st === 'missing');
  if (problems.length === 0) return null;

  let label: string;
  if (problems.length === 2 && problems[0].st === problems[1].st) {
    label = `Kamera & Mikrofon ${word(problems[0].st)}`;
  } else if (problems.length === 2) {
    label = `${problems[0].name} ${word(problems[0].st)} · ${problems[1].name} ${word(problems[1].st)}`;
  } else {
    label = `${problems[0].name} ${word(problems[0].st)}`;
  }

  const anyMissing = problems.some((p) => p.st === 'missing');
  const tip = anyMissing
    ? 'Kein Kamera-/Mikrofon-Gerät verfügbar. Bitte ein Gerät anschließen. Klicken, um die Geräte zu prüfen.'
    : 'Kamera/Mikrofon sind im Browser blockiert. Klicken, um die Geräte zu prüfen und freizugeben.';

  return (
    <div
      className="header-chip appearing"
      onClick={() => openSettings('devices')}
      title={tip}
      style={{
        display: 'inline-flex', cursor: 'pointer', maxWidth: 230, letterSpacing: '.02em',
        padding: '4px 9px',
        background: 'rgba(239,68,68,.14)', borderColor: 'var(--danger)', color: 'var(--danger)',
      }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{label}</span>
    </div>
  );
}

function CapRow({ icon, name, state, suffix }: { icon: string; name: string; state: CapabilityState; suffix?: string }) {
  const label =
    state === 'ok' ? 'Aktiv' + (suffix ?? '') : state === 'bad' ? 'Blockiert' : state === 'miss' ? 'Nicht aktiv' : '…';
  const cls = state === 'ok' ? 'ok' : state === 'bad' ? 'bad' : 'miss';
  return (
    <div className="reach-cap-row">
      <div className="cap-name">
        {icon} {name}
      </div>
      <div className={`cap-state ${cls}`}>{label}</div>
    </div>
  );
}

export function Header() {
  // pre250: bereits freigegebenes Telefonie-Headset automatisch wieder koppeln
  useEffect(() => { void autoReconnectHeadset(); }, []);
  const openCmdK = useUIStore((s) => s.openCmdK);
  const cmdkQuery = useUIStore((s) => s.cmdkQuery);
  const setCmdkQuery = useUIStore((s) => s.setCmdkQuery);
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onFocusSearch = () => searchRef.current?.focus();
    window.addEventListener('bamoa:focus-search', onFocusSearch);
    return () => window.removeEventListener('bamoa:focus-search', onFocusSearch);
  }, []);
  const toggleSettings = useUIStore((s) => s.toggleSettings);
  const openHelp = useUIStore((s) => s.openHelp);
  const nfcVisible = useUIStore((s) => s.nfcChipVisible);
  const networkVisible = useUIStore((s) => s.networkChipVisible);
  const companionConnected = useUIStore((s) => s.companionConnected);
  // Welle 6f: User-Menu mit Logout
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const displayName = getDisplayName() ?? 'Berater:in';
  const tenantName = getTenantName() ?? '';
  const tenantLogo = getTenantLogo();
  const initials = displayName.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();

  // Click-Outside zum Schließen des User-Menüs
  useEffect(() => {
    if (!userMenuOpen) return;
    function onDocClick(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [userMenuOpen]);

  return (
    <header className="header">
      <div className="header-left">
        <div
          className="user-block"
          ref={userMenuRef}
          onClick={() => setUserMenuOpen((v) => !v)}
          style={{ cursor: 'pointer', position: 'relative', userSelect: 'none' }}
          title="Menü öffnen"
        >
          {tenantLogo
            ? <img src={tenantLogo} alt="" className="tenant-logo-btn" style={{ height: 28, maxWidth: 140, objectFit: 'contain', marginRight: 6 }} />
            : <div className="avatar">{initials}</div>}
          <div className="user-info">
            <div className="user-name">{displayName}</div>
            <div className="user-mandant">{tenantName}</div>
          </div>
          {userMenuOpen && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                top: 'calc(100% + 6px)',
                left: 0,
                minWidth: 200,
                background: 'var(--bg-elev-2, #1a1a1a)',
                border: '1px solid var(--hairline, rgba(255,255,255,0.12))',
                borderRadius: 6,
                boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
                zIndex: 1000,
                padding: 4,
                color: 'var(--fg, #e8e8e8)',
              }}
            >
              <button
                onClick={() => {
                  setUserMenuOpen(false);
                  if (confirmLogout()) clearToken();
                }}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  background: 'transparent',
                  color: 'var(--fg, #e8e8e8)',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
                onMouseEnter={(e) => { (e.target as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; }}
                onMouseLeave={(e) => { (e.target as HTMLElement).style.background = 'transparent'; }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Abmelden
              </button>
            </div>
          )}
        </div>

        <StatusSwitcher />

        {companionConnected && (
          <div className="header-chip companion" title="Companion verbunden">
            <span className="chip-dot"></span>
            Companion
          </div>
        )}

        {nfcVisible && (
          <div
            className="header-chip appearing"
            style={{
              display: 'inline-flex',
              background: 'rgba(59,130,246,0.12)',
              borderColor: 'rgba(59,130,246,0.35)',
              color: '#93c5fd',
            }}
          >
            <span className="chip-dot" style={{ background: 'currentColor' }}></span>
            EC-Karte erkannt
          </div>
        )}
        {networkVisible && (
          <div className="header-chip warn appearing" style={{ display: 'inline-flex' }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            Schwacher Empfang
          </div>
        )}
      </div>

      <div className="header-search cmdk-center" onMouseDown={(e) => { if (e.target !== searchRef.current) { e.preventDefault(); searchRef.current?.focus(); } openCmdK(); }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
        <input
          ref={searchRef}
          type="text"
          className="header-search-input"
          placeholder="Suche &amp; Aktionen"
          value={cmdkQuery}
          onChange={(e) => setCmdkQuery(e.target.value)}
          onFocus={openCmdK}
        />
        <kbd>⌘K</kbd>
      </div>

      <div className="header-right">
        <DeviceHealthChip />
        <ReachIcon />
        <button className="icon-btn" onClick={() => openHelp()} title="Hilfe (?)">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 2.5-3 4" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </button>
        <button className="icon-btn" onClick={toggleSettings} title="Einstellungen">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </button>
      </div>
    </header>
  );
}
